import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, ArrowRight, Star, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function FanZoneTeaser({ match }) {
    const [scoreHome, setScoreHome] = useState('');
    const [scoreAway, setScoreAway] = useState('');

    // Données par défaut (fallback)
    const defaultHome = { name: 'EFAFC', logo: null, short: 'EFAFC' };
    const defaultAway = { name: 'Adversaire', logo: null, short: 'ADV' };

    const homeTeam = match ? {
        name: match.home.short_name_federation || match.home.name,
        logo: match.home.logo_path,
        short: match.home.short_name || 'EFAFC'
    } : defaultHome;

    const awayTeam = match ? {
        name: match.away.short_name_federation || match.away.name,
        logo: match.away.logo_path,
        short: match.away.short_name || 'ADV'
    } : defaultAway;

    return (
        <section className="w-full py-16 px-4 relative overflow-hidden">
            <div className="container mx-auto relative z-10 max-w-6xl border border-white/10 rounded-2xl p-6 bg-white/10 backdrop-blur-sm">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

                    {/* Left Content */}
                    <div className="flex-1 text-center lg:text-left space-y-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/10 text-efafcYellow rounded-full font-bold text-sm shadow-lg"
                        >
                            <Trophy size={16} />
                            FAN ZONE
                        </motion.div>

                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-title font-bold text-white leading-tight">
                            DEVINEZ LE <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-efafcYellow to-efafcGreen">
                                SCORE DU MATCH
                            </span>
                        </h2>

                        <p className="text-gray-300 text-lg max-w-xl mx-auto lg:mx-0 font-body leading-relaxed">
                            Prouvez votre expertise ! Rejoignez la communauté, faites vos pronostics et tentez de gagner des récompenses exclusives chaque mois.
                        </p>

                        <div className="flex items-center gap-8 justify-center lg:justify-start text-white/80 text-sm font-accent">
                            <div className="flex items-center gap-3 bg-white/5 px-4 py-2 rounded-xl border border-white/5">
                                <div className="p-2 bg-efafcYellow/20 rounded-lg text-efafcYellow">
                                    <Star size={18} />
                                </div>
                                <div className="text-left">
                                    <p className="font-bold text-white">Classement</p>
                                    <p className="text-xs text-gray-400">Mensuel & Annuel</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 bg-white/5 px-4 py-2 rounded-xl border border-white/5">
                                <div className="p-2 bg-efafcGreen/20 rounded-lg text-efafcGreen">
                                    <Users size={18} />
                                </div>
                                <div className="text-left">
                                    <p className="font-bold text-white">+500 Fans</p>
                                    <p className="text-xs text-gray-400">Communauté active</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Content: Interactive Card */}
                    <motion.div
                        className="w-full max-w-md p-8 relative overflow-hidden group"
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                    >

                        <div className="relative z-10 border border-white/10 rounded-2xl p-6">
                            <div className="text-center mb-8">
                                <h3 className="text-white font-title font-bold text-xl mb-2 tracking-wide">VOTRE PRONOSTIC</h3>
                                <p className="text-gray-400 text-sm font-accent truncate px-4">
                                    {homeTeam.name} vs {awayTeam.name}
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-6 mb-10">
                                <div className="flex flex-col items-center gap-3">
                                    {homeTeam.logo ? (
                                        <img src={homeTeam.logo} alt={homeTeam.name} className="w-12 h-12 object-contain mb-1" />
                                    ) : (
                                        <span className="font-bold text-efafcYellow font-title tracking-wider">{homeTeam.short}</span>
                                    )}
                                    <input
                                        type="number"
                                        value={scoreHome}
                                        onChange={(e) => setScoreHome(e.target.value)}
                                        className="w-20 h-20 bg-black/30 backdrop-blur-sm rounded-2xl text-center text-3xl font-bold text-white border border-white/10 focus:border-efafcYellow focus:ring-2 focus:ring-efafcYellow/20 focus:outline-none transition-all placeholder-white/20"
                                        placeholder="-"
                                    />
                                </div>
                                <span className="text-4xl font-bold text-white/20">:</span>
                                <div className="flex flex-col items-center gap-3">
                                    {awayTeam.logo ? (
                                        <img src={awayTeam.logo} alt={awayTeam.name} className="w-12 h-12 object-contain mb-1" />
                                    ) : (
                                        <span className="font-bold text-white font-title tracking-wider">{awayTeam.short}</span>
                                    )}
                                    <input
                                        type="number"
                                        value={scoreAway}
                                        onChange={(e) => setScoreAway(e.target.value)}
                                        className="w-20 h-20 bg-black/30 backdrop-blur-sm rounded-2xl text-center text-3xl font-bold text-white border border-white/10 focus:border-efafcGreen focus:ring-2 focus:ring-efafcGreen/20 focus:outline-none transition-all placeholder-white/20"
                                        placeholder="-"
                                    />
                                </div>
                            </div>

                            <Link to="/fanzone">
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="w-full py-4 bg-gradient-to-r from-efafcYellow to-efafcGreen text-efafcDark rounded-xl font-bold font-accent shadow-lg shadow-efafcGreen/20 flex items-center justify-center gap-2 group"
                                >
                                    VALIDER MON PRONO
                                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                                </motion.button>
                            </Link>

                            <p className="text-center text-xs text-gray-500 mt-6 font-accent">
                                Connectez-vous pour sauvegarder vos points
                            </p>
                        </div>
                    </motion.div>

                </div>
            </div>
        </section>
    );
}
